import { useCallback, useEffect, useRef } from 'react';

export interface UsePromptHistoryResult {
  /** Перейти к более старому промпту. Возвращает текст или null, если двигаться некуда. */
  prev: (current: string) => string | null;
  /** Перейти к более новому промпту. На выходе из истории возвращает сохранённый черновик. */
  next: () => string | null;
  /** Сбросить позицию навигации (например, при ручном вводе). */
  reset: () => void;
}

/**
 * Навигация по истории отправленных промптов стрелками ↑/↓ как в терминале.
 *
 * Источник истории — переданный массив `history` (промпты пользователя из
 * текущего чата), где history[0] — самый старый, последний элемент — самый
 * свежий. ↑ движется от свежих к старым, ↓ — обратно; при выходе за пределы
 * свежих возвращается черновик, сохранённый в момент входа в навигацию.
 */
export function usePromptHistory(history: string[]): UsePromptHistoryResult {
  const historyRef = useRef(history);
  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  // null — навигация не активна (показывается черновик пользователя).
  const indexRef = useRef<number | null>(null);
  const draftRef = useRef('');

  const reset = useCallback(() => {
    indexRef.current = null;
    draftRef.current = '';
  }, []);

  const prev = useCallback((current: string): string | null => {
    const history = historyRef.current;
    if (history.length === 0) return null;

    if (indexRef.current === null) {
      // Входим в навигацию: запоминаем черновик и берём самый свежий промпт.
      draftRef.current = current;
      indexRef.current = history.length - 1;
    } else if (indexRef.current > 0) {
      indexRef.current -= 1;
    } else {
      // Уже на самом старом — остаёмся.
      return null;
    }

    return history[indexRef.current] ?? null;
  }, []);

  const next = useCallback((): string | null => {
    const history = historyRef.current;
    if (indexRef.current === null) return null;

    if (indexRef.current < history.length - 1) {
      indexRef.current += 1;
      return history[indexRef.current] ?? null;
    }

    // Вышли за пределы свежих элементов — возвращаем черновик.
    const draft = draftRef.current;
    reset();
    return draft;
  }, [reset]);

  return { prev, next, reset };
}
